import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Sidebar from "@/components/Sidebar";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ConfirmRoot } from "@/lib/confirm";
import { Toaster } from "sonner";

import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import PatientsPage from "@/pages/PatientsPage";
import PatientDetailPage from "@/pages/PatientDetailPage";
import SurgeonsPage from "@/pages/SurgeonsPage";
import AnesthesiologistsPage from "@/pages/AnesthesiologistsPage";
import InstrumentTechsPage from "@/pages/InstrumentTechsPage";
import HospitalsPage from "@/pages/HospitalsPage";
import ProceduresPage from "@/pages/ProceduresPage";
import ProcedureDetailPage from "@/pages/ProcedureDetailPage";
import RawMapsPage from "@/pages/RawMapsPage";
import FilteredMapPage from "@/pages/FilteredMapPage";
import CasesPage from "@/pages/CasesPage";
import CaseDetailPage from "@/pages/CaseDetailPage";
import UsersPage from "@/pages/UsersPage";
import EmpresasPage from "@/pages/EmpresasPage";
import ImportPage from "@/pages/ImportPage";
import SettingsPage from "@/pages/SettingsPage";
import StubPage from "@/pages/StubPage";
import ProducaoPage from "@/pages/ProducaoPage";
import FaturamentoPage from "@/pages/FaturamentoPage";
import FinanceiroPage from "@/pages/FinanceiroPage";
import ControleBvsmPage from "@/pages/ControleBvsmPage";
import ProBonoPage from "@/pages/ProBonoPage";
import RelatoriosPage from "@/pages/RelatoriosPage";
import AssistenteIAPage from "@/pages/AssistenteIAPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AliasesPage from "@/pages/AliasesPage";

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <Sidebar>{children}</Sidebar>
    </ProtectedRoute>
  );
}

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker
          .register("/service-worker.js")
          .catch((err) => console.warn("SW registration failed:", err));
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <PWAInstallPrompt />
          <ConfirmRoot />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<Navigate to="/painel" replace />} />
            <Route path="/painel" element={<Shell><DashboardPage /></Shell>} />

            <Route path="/pacientes" element={<Shell><PatientsPage /></Shell>} />
            <Route path="/pacientes/:id" element={<Shell><PatientDetailPage /></Shell>} />

            <Route path="/cirurgioes" element={<Shell><SurgeonsPage /></Shell>} />
            <Route path="/anestesistas" element={<Shell><AnesthesiologistsPage /></Shell>} />
            <Route path="/instrumentadores" element={<Shell><InstrumentTechsPage /></Shell>} />
            <Route path="/hospitais" element={<Shell><HospitalsPage /></Shell>} />
            <Route path="/procedimentos" element={<Shell><ProceduresPage /></Shell>} />
            <Route path="/procedimentos/:id" element={<Shell><ProcedureDetailPage /></Shell>} />
            <Route path="/aliases" element={<Shell><AliasesPage /></Shell>} />

            <Route path="/mapa-bruto" element={<Shell><RawMapsPage /></Shell>} />
            <Route path="/mapa-filtrado" element={<Shell><FilteredMapPage /></Shell>} />
            <Route path="/mapa-filtrado/:id" element={<Shell><FilteredMapPage /></Shell>} />

            <Route path="/casos" element={<Shell><CasesPage /></Shell>} />
            <Route path="/casos/:id" element={<Shell><CaseDetailPage /></Shell>} />

            <Route path="/usuarios" element={<Shell><UsersPage /></Shell>} />
            <Route path="/empresas" element={<Shell><EmpresasPage /></Shell>} />
            <Route path="/importar" element={<Shell><ImportPage /></Shell>} />
            <Route path="/configuracoes" element={<Shell><SettingsPage /></Shell>} />

            <Route path="/producao" element={<Shell><ProducaoPage /></Shell>} />
            <Route path="/faturamento" element={<Shell><FaturamentoPage /></Shell>} />
            <Route path="/financeiro" element={<Shell><FinanceiroPage /></Shell>} />
            <Route path="/controle-bvsm" element={<Shell><ControleBvsmPage /></Shell>} />
            <Route path="/pro-bono" element={<Shell><ProBonoPage /></Shell>} />
            <Route path="/relatorios" element={<Shell><RelatoriosPage /></Shell>} />
            <Route path="/analytics" element={<Shell><AnalyticsPage /></Shell>} />
            <Route path="/assistente-ia" element={<Shell><AssistenteIAPage /></Shell>} />

            <Route path="*" element={<Navigate to="/painel" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
